import React, { useState, useCallback, useEffect } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import debounce from 'lodash/debounce';
import {
    AddressSuggestion,
    NormalizedAddress
} from '../../types/address';
import { constructionService } from '../../services/construction';

interface AddressInputProps {
    value: string;
    onChange: (
        address: string,
        suggestion?: AddressSuggestion | null
    ) => void;
    error?: boolean;
    helperText?: string;
}

export const AddressInput: React.FC<AddressInputProps> = ({
    value,
    onChange,
    error,
    helperText
}) => {
    const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState(value || '');
    const [selectedValue, setSelectedValue] = useState<AddressSuggestion | null>(null);


    useEffect(() => {
        setInputValue(value || '');
        // 新しい値が設定された時、selectedValueもリセット
        setSelectedValue(value ? {
            address: {
                originalAddress: value,
                normalizedAddress: value,
                prefecture: '',
                city: '',
                street: '',
                postalCode: ''
            }
        } : null);
    }, [value]);

    // NormalizedAddressをAddressSuggestionに変換する関数
    const convertToAddressSuggestion = (normalizedAddress: NormalizedAddress): AddressSuggestion => {
        return {
            address: {
                originalAddress: normalizedAddress.originalAddress,
                normalizedAddress: normalizedAddress.normalizedAddress,
                prefecture: normalizedAddress.prefecture,
                city: normalizedAddress.city,
                street: normalizedAddress.street,
                postalCode: normalizedAddress.postalCode
            }
        };
    };

    const searchAddress = useCallback(
        debounce(async (searchText: string) => {
            if (searchText.length < 3) return;

            setLoading(true);
            try {
                const results = await constructionService.searchAddress(searchText);
                
                const convertedResults = results.map(result => ({
                    address: {
                        originalAddress: result.originalAddress,
                        normalizedAddress: result.normalizedAddress,
                        prefecture: result.details.prefecture,
                        city: result.details.city,
                        street: result.details.street,
                        postalCode: result.details.postalCode
                    },
                    routeId: result.routeId,  // APIレスポンスからrouteIdを取得
                    routeInfo: {
                        ID: result.routeId || result.id,  // routeIdがある場合はそれを使用
                        Normalized_Address_ID: result.id,
                        Distance: Number(result.details.distance),
                        Light_Vehicle_Toll: result.details.lightVehicleToll,
                        Standard_Vehicle_Toll: result.details.standardVehicleToll,
                        Medium_Vehicle_Toll: result.details.mediumVehicleToll,
                        Large_Vehicle_Toll: result.details.largeVehicleToll,
                        distanceInKm: (Number(result.details.distance) / 1000).toFixed(1),
                        Calculated_At: result.details.calculatedAt
                    }
                }));
                setSuggestions(convertedResults);
            } catch (error) {
                console.error('住所検索エラー:', error);
                setSuggestions([]);
            } finally {
                setLoading(false);
            }
        }, 500),
        []
    );

    const normalizeAddress = async (address: string) => {
        try {
            const normalized = await constructionService.normalizeAddress(address);
            if (normalized) {
                // NormalizedAddress から AddressSuggestion に変換
                const suggestion: AddressSuggestion = {
                    address: {
                        originalAddress: normalized.originalAddress,
                        normalizedAddress: normalized.normalizedAddress,
                        prefecture: normalized.prefecture,
                        city: normalized.city,
                        street: normalized.street,
                        postalCode: normalized.postalCode
                    }
                };
                onChange(address, suggestion);
            } else {
                onChange(address, null);
            }
        } catch (error) {
            console.error('住所正規化エラー:', error);
            onChange(address, null);
        }
    };



    return (
        <Autocomplete
            fullWidth
            freeSolo
            options={suggestions}
            getOptionLabel={(option) =>
                typeof option === 'string'
                    ? option
                    : option.address.normalizedAddress
            }
            value={selectedValue}  // valueをselectedValueに変更
            inputValue={inputValue}
            loading={loading}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="住所"
                    error={error}
                    helperText={helperText}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <React.Fragment>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}
            onInputChange={(_, newInputValue) => {
                setInputValue(newInputValue);
                onChange(newInputValue, null);
                searchAddress(newInputValue);
            }}
            onChange={(_, newValue) => {
                if (typeof newValue === 'string') {
                    setInputValue(newValue);
                    onChange(newValue, null);
                    normalizeAddress(newValue);
                } else if (newValue) {
                    setInputValue(newValue.address.normalizedAddress);
                    onChange(newValue.address.normalizedAddress, newValue);
                } else {
                    setInputValue('');
                    onChange('', null);
                }
            }}
            renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                    <li key={key} {...otherProps}>
                        {typeof option === 'string' ? option : (
                            <>
                                {option.address.normalizedAddress}
                                <br />
                                <small style={{ color: 'gray' }}>
                                    {option.address.postalCode && `〒${option.address.postalCode}`}
                                    {option.address.prefecture}{option.address.city}{option.address.street}
                                    {option.routeInfo && ` (${option.routeInfo.distanceInKm}km)`}
                                </small>
                            </>
                        )}
                    </li>
                );
            }}
        />
    );
};

export default AddressInput;